import styled from 'styled-components';

export const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    padding: 0 16px 20px;
  }

  @media (min-width: 821px) and (max-width: 1024px) {
    padding: 0 20px 20px;
  }

  @media (max-width: 767px) {
    padding: 0 12px 16px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.colors?.text || '#111827'};
  margin: 0;
`;

export const ItemCount = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.colors?.textSecondary || '#6B7280'};
`;

export const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  /* Ensure button content is centered */
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;
`;

export const FilterDropdownWrapper = styled.div`
  min-width: 140px;
`;

export const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** Encabezado de grupo por mes/año (ej. "JANUARY 2026"). No se muestra para el mes actual. */
export const MonthGroupHeader = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors?.textSecondary || '#6B7280'};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin: 16px 0;
`;

export const NotesGroupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
`;

export const EmptyIcon = styled.div`
  color: ${(props) => props.theme.colors?.textSecondary || '#9CA3AF'};
  margin-bottom: 16px;
`;

export const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => props.theme.colors?.text || '#111827'};
  margin: 0 0 8px 0;
`;

export const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${(props) => props.theme.colors?.textSecondary || '#6B7280'};
  margin: 0 0 16px 0;

  & + button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

export const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => props.theme.colors?.border || '#E5E7EB'};
`;

export const PaginationInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const PaginationText = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.colors?.textSecondary || '#6B7280'};
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SkeletonCard = styled.div`
  height: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// Selection Action Bar — Figma node 8455-48926. Flotante a 145px al hacer scroll.
const SELECTION_BAR_FLOATING_TOP = 192;
const SELECTION_BAR_HEIGHT = 60;

export const SelectionActionBarAnchor = styled.div`
  width: 100%;
  height: ${(props) => (props.$isFloating ? `${SELECTION_BAR_HEIGHT}px` : '0')};
  flex-shrink: 0;
`;

export const SelectionActionBar = styled.div`
  display: flex;
  width: 100%;
  max-width: none;
  padding: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  border-radius: 8px;
  border: 0.5px solid #80AFFF;
  background: #ffffff;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  margin-top: ${(props) => (props.$isFloating ? '0' : '-56px')};
  margin-bottom: 12px;
  position: ${(props) => (props.$isFloating ? 'fixed' : 'relative')};
  top: ${(props) => (props.$isFloating ? `${SELECTION_BAR_FLOATING_TOP}px` : 'auto')};
  left: ${(props) => (props.$isFloating ? '50%' : 'auto')};
  transform: ${(props) => (props.$isFloating ? 'translateX(-50%)' : 'none')};
  z-index: ${(props) => (props.$isFloating ? 48 : 1)};
  box-shadow: 0 4px 16px rgba(9, 30, 66, 0.08);
  box-sizing: border-box;
  z-index: 999;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    max-width: none;
    padding: ${(props) => (props.$isFloating ? '10px 12px' : '12px')};
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: none;
    padding: ${(props) => (props.$isFloating ? '8px 12px' : '12px')};
  }
`;

export const SelectionActionBarInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  width: 100%;
`;

export const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SelectAllButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: #0a0a0a;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #e9ebef;
    color: #030213;
  }
`;

export const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #0a0a0a;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #e9ebef;
    color: #030213;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CloseSelectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: #ffffff;
  border-radius: 6px;
  cursor: pointer;
  color: #0a0a0a;
  margin-left: 8px;
  transition: all 0.15s ease;

  &:hover {
    background: #e9ebef;
    color: #030213;
  }
`;
